import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Spinner,
  Badge,
  Button,
} from 'reactstrap';

import BreadCrumb from '../../../Components/Common/BreadCrumb';
import TableContainer from '../../../Components/Common/TableContainer';

import { withTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import campaignService, {
  downloadCampaignsCSV,
} from '../../../helpers/campaign_service';
import { getCampaignErrorMessage } from '../../../helpers/campaignErrorHandler';
import CreateCampaignWizard from './CreateCampaignWizard';
import EmptyList from '../../../Components/Common/EmptyList';
import ExportReport from '../../../Components/Common/ExportReport';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const formatDateHelper = (dateString, locale = 'pt') => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const brazilOffset = -3 * 60;
  const localOffset = date.getTimezoneOffset();
  const adjustedDate = new Date(
    date.getTime() + (brazilOffset + localOffset) * 60 * 1000
  );
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  return new Intl.DateTimeFormat(
    locale === 'pt' ? 'pt-BR' : 'en-US',
    options
  ).format(adjustedDate);
};

const formatDateOnly = (dateString, locale = 'pt') => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';
  const brazilOffset = -3 * 60;
  const localOffset = date.getTimezoneOffset();
  const adjustedDate = new Date(
    date.getTime() + (brazilOffset + localOffset) * 60 * 1000
  );
  const options = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
  return new Intl.DateTimeFormat(
    locale === 'pt' ? 'pt-BR' : 'en-US',
    options
  ).format(adjustedDate);
};

const Campaigns = (props) => {
  document.title = 'Campanhas | Opencashback';

  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [meta, setMeta] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState([]);

  const fetchCampaigns = async (pageNumber) => {
    setIsLoading(true);
    try {
      const response = await campaignService.getCampaigns({
        page: pageNumber,
        limit: 10,
        order: 'desc',
      });
      setCampaigns(response.data);
      setMeta(response.meta);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error(props.t('campaigns.messages.fetch-error'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllCampaigns = async () => {
    try {
      const response = await campaignService.getCampaigns({
        page: 1,
        limit: 100,
        order: 'desc',
      });
      setAllCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching all campaigns:', error);
    }
  };

  useEffect(() => {
    fetchCampaigns(page);
  }, [page]);

  const handleCampaignCreated = () => {
    fetchCampaigns(1);
    fetchAllCampaigns();
    setPage(1);
    toast.success(props.t('campaigns.create-success'));
  };

  const getStatusBadgeClasses = (campaign) => {
    const status = campaign.status;
    const now = new Date();
    const startDate = campaign.startDate ? new Date(campaign.startDate) : null;
    const endDate = campaign.endDate ? new Date(campaign.endDate) : null;

    if (status === 'active' && startDate && endDate) {
      if (now >= startDate && now <= endDate) {
        return 'bg-success-subtle text-success';
      } else if (now < startDate) {
        return 'bg-info-subtle text-info';
      } else if (now > endDate) {
        return 'bg-secondary-subtle text-secondary';
      }
    }

    switch (status) {
      case 'active':
        return 'bg-success-subtle text-success';
      case 'scheduled':
        return 'bg-info-subtle text-info';
      case 'completed':
        return 'bg-secondary-subtle text-secondary';
      case 'paused':
        return 'bg-warning-subtle text-warning';
      case 'canceled':
      case 'cancelled':
        return 'bg-danger-subtle text-danger';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  };

  const getStatusLabel = (campaign) => {
    const status = campaign.status;
    const now = new Date();
    const startDate = campaign.startDate ? new Date(campaign.startDate) : null;
    const endDate = campaign.endDate ? new Date(campaign.endDate) : null;

    if (status === 'active' && startDate && endDate) {
      if (now >= startDate && now <= endDate) {
        return props.t('campaigns.status-active');
      } else if (now < startDate) {
        return props.t('campaigns.status-scheduled');
      } else if (now > endDate) {
        return props.t('campaigns.status-completed');
      }
    }

    switch (status) {
      case 'active':
        return props.t('campaigns.status-active');
      case 'scheduled':
        return props.t('campaigns.status-scheduled');
      case 'completed':
        return props.t('campaigns.status-completed');
      case 'paused':
        return props.t('campaigns.status-paused');
      case 'canceled':
      case 'cancelled':
        return props.t('campaigns.status-canceled');
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    const locale = props.i18n.language === 'pt' ? 'pt' : 'en';
    return formatDateHelper(dateString, locale);
  };

  const formatDateOnlyFormatted = (dateString) => {
    const locale = props.i18n.language === 'pt' ? 'pt' : 'en';
    return formatDateOnly(dateString, locale);
  };

  const formatCashback = (value, type) => {
    if (type === 'percentage') {
      return `${value}%`;
    } else if (type === 'fixed') {
      const numValue =
        typeof value === 'number' ? value : parseFloat(value) || 0;
      return `R$ ${numValue.toFixed(2)}`;
    }
    return '-';
  };

  const formatMaxRedemption = (value, type) => {
    if (type === 'percentage') {
      return `${value}%`;
    } else if (type === 'fixed') {
      const numValue =
        typeof value === 'number' ? value : parseFloat(value) || 0;
      return `R$ ${numValue.toFixed(2)}`;
    }
    return '-';
  };

  const handlePauseReactivate = useCallback(
    async (campaignId, currentStatus) => {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const successMessage =
        newStatus === 'paused'
          ? props.t('campaigns.messages.pause-success')
          : props.t('campaigns.messages.reactivate-success');

      try {
        await campaignService.updateCampaign(campaignId, { status: newStatus });

        setCampaigns((prevCampaigns) =>
          prevCampaigns.map((campaign) =>
            campaign.id === campaignId
              ? {
                  ...campaign,
                  status: newStatus,
                  isActive: newStatus === 'active',
                }
              : campaign
          )
        );
        setAllCampaigns((prevCampaigns) =>
          prevCampaigns.map((campaign) =>
            campaign.id === campaignId
              ? {
                  ...campaign,
                  status: newStatus,
                  isActive: newStatus === 'active',
                }
              : campaign
          )
        );
        toast.success(successMessage);
      } catch (error) {
        const errorMessage = getCampaignErrorMessage(error, props.t);
        toast.error(errorMessage);
      }
    },
    [props]
  );

  const handleCancel = useCallback(
    async (campaignId) => {
      if (window.confirm(props.t('campaigns.messages.cancel-confirm'))) {
        try {
          await campaignService.updateCampaign(campaignId, {
            status: 'cancelled',
          });

          setCampaigns((prevCampaigns) =>
            prevCampaigns.map((campaign) =>
              campaign.id === campaignId
                ? { ...campaign, status: 'cancelled', isActive: false }
                : campaign
            )
          );
          setAllCampaigns((prevCampaigns) =>
            prevCampaigns.map((campaign) =>
              campaign.id === campaignId
                ? { ...campaign, status: 'cancelled', isActive: false }
                : campaign
            )
          );
          toast.success(props.t('campaigns.messages.cancel-success'));
        } catch (error) {
          const errorMessage = getCampaignErrorMessage(error, props.t);
          toast.error(errorMessage);
        }
      }
    },
    [props]
  );

  const handleEdit = useCallback(
    (campaignId) => {
      navigate(`/estrategias/campanhas/${campaignId}/editar`);
    },
    [navigate]
  );

  const handleExport = useCallback(() => {
    try {
      downloadCampaignsCSV(allCampaigns, 'campanhas.csv');
      toast.success(props.t('campaigns.messages.export-success'));
    } catch (error) {
      console.error('Error exporting campaigns:', error);
      toast.error(props.t('campaigns.messages.export-error'));
    }
  }, [allCampaigns, props]);

  const columns = useMemo(
    () => [
      {
        Header: props.t('campaigns.table.name'),
        accessor: 'name',
        Cell: (data) => (
          <span className="fw-semibold">{data.row.original.name}</span>
        ),
      },
      {
        Header: props.t('campaigns.table.validity-period'),
        accessor: 'period',
        Cell: (data) => {
          const { startDate, endDate } = data.row.original;
          return (
            <span>
              {formatDate(startDate)} - {formatDate(endDate)}
            </span>
          );
        },
      },
      {
        Header: props.t('campaigns.table.start'),
        accessor: 'startDate',
        Cell: (data) => formatDateOnlyFormatted(data.row.original.startDate),
      },
      {
        Header: props.t('campaigns.table.end'),
        accessor: 'endDate',
        Cell: (data) => formatDateOnlyFormatted(data.row.original.endDate),
      },
      {
        Header: props.t('campaigns.table.cashback-generation'),
        accessor: 'cashback',
        Cell: (data) => {
          const { cashbackValue, cashbackType } = data.row.original;
          return formatCashback(cashbackValue, cashbackType);
        },
      },
      {
        Header: props.t('campaigns.table.max-redemption'),
        accessor: 'maxRedemption',
        Cell: (data) => {
          const { maxRedemptionValue, maxRedemptionType } = data.row.original;
          return formatMaxRedemption(maxRedemptionValue, maxRedemptionType);
        },
      },
      {
        Header: props.t('campaigns.table.status'),
        accessor: 'status',
        Cell: (data) => (
          <Badge
            className={`bg-gradient ${getStatusBadgeClasses(data.row.original)}`}
          >
            {getStatusLabel(data.row.original)}
          </Badge>
        ),
      },
      {
        Header: props.t('campaigns.table.actions'),
        accessor: 'actions',
        disableSortBy: true,
        Cell: (data) => {
          const { status, id, startDate, endDate } = data.row.original;

          const now = new Date();
          const start = startDate ? new Date(startDate) : null;
          const end = endDate ? new Date(endDate) : null;
          const isVigente =
            status === 'active' && start && end && now >= start && now <= end;
          const isScheduled =
            status === 'scheduled' ||
            (status === 'active' && start && now < start);
          const isCompleted = status === 'active' && end && now > end;
          const isPaused = status === 'paused';
          const isCanceled = status === 'cancelled';

          const canCancel = !isCanceled && !isCompleted;
          const canPauseReactivate = isVigente || isPaused;
          const canEdit = isScheduled && !isVigente;

          return (
            <div className="d-flex gap-2">
              {canEdit && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => handleEdit(id)}
                  title={props.t('campaigns.edit')}
                >
                  <i className="bx bx-edit"></i>
                </Button>
              )}

              {canPauseReactivate && (
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => handlePauseReactivate(id, status)}
                  title={
                    isPaused
                      ? props.t('campaigns.reactivate')
                      : props.t('campaigns.pause')
                  }
                >
                  <i className={isPaused ? 'bx bx-play' : 'bx bx-pause'}></i>
                </Button>
              )}

              {canCancel && (
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleCancel(id)}
                  title={props.t('campaigns.cancel')}
                >
                  <i className="bx bx-x"></i>
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    [props, handleEdit, handlePauseReactivate, handleCancel]
  );

  const breadcrumbItems = [
    { label: props.t('strategy-title-menu'), link: '/estrategias/conta' },
    { label: props.t('campaigns.breadcrumb'), active: true },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={props.t('campaigns.title')}
            breadcrumbItems={breadcrumbItems}
          />

          <Row>
            <Col xs="12">
              <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <h4 className="card-title mb-0">
                    {props.t('campaigns.title')}
                  </h4>
                  <div className="d-flex gap-2">
                    <ExportReport
                      filename="Relatório OCK: Campanhas"
                      service="campaigns"
                      pages={meta.totalPages}
                      disabled={isLoading || campaigns.length === 0}
                      columns={[
                        {
                          accessor: 'name',
                          Header: props.t('campaigns.table.name'),
                        },
                        {
                          accessor: 'startDate',
                          Header: props.t('campaigns.table.start'),
                        },
                        {
                          accessor: 'endDate',
                          Header: props.t('campaigns.table.end'),
                        },
                        {
                          accessor: 'cashback',
                          Header: props.t(
                            'campaigns.table.cashback-generation'
                          ),
                          reportProps: {
                            formatter: {
                              type: 'custom',
                              properties: {
                                format: (row) =>
                                  formatCashback(
                                    row.cashbackValue,
                                    row.cashbackType
                                  ),
                              },
                            },
                          },
                        },
                        {
                          accessor: 'maxRedemption',
                          Header: props.t('campaigns.table.max-redemption'),
                          reportProps: {
                            formatter: {
                              type: 'custom',
                              properties: {
                                format: (row) =>
                                  formatMaxRedemption(
                                    row.maxRedemptionValue,
                                    row.maxRedemptionType
                                  ),
                              },
                            },
                          },
                        },
                        {
                          accessor: 'status',
                          Header: props.t('campaigns.table.status'),
                          reportProps: {
                            formatter: {
                              type: 'enum',
                              properties: {
                                options: [
                                  {
                                    key: 'scheduled',
                                    value: props.t(
                                      'campaigns.status-scheduled'
                                    ),
                                  },
                                  {
                                    key: 'active',
                                    value: props.t('campaigns.status-active'),
                                  },
                                  {
                                    key: 'completed',
                                    value: props.t(
                                      'campaigns.status-completed'
                                    ),
                                  },
                                  {
                                    key: 'paused',
                                    value: props.t('campaigns.status-paused'),
                                  },
                                  {
                                    key: 'canceled',
                                    value: props.t('campaigns.status-canceled'),
                                  },
                                ],
                              },
                            },
                          },
                        },
                      ]}
                    />

                    <Button
                      color="success"
                      onClick={() => setShowCreateModal(true)}
                      className="add-btn"
                      id="create-btn"
                    >
                      <i className="ri-add-line align-bottom me-1"></i>
                      {props.t('campaigns.create-button')}
                    </Button>
                  </div>
                </CardHeader>
                <CardBody>
                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" size="lg" />
                    </div>
                  ) : campaigns.length === 0 ? (
                    <EmptyList
                      title={props.t('campaigns.empty.title')}
                      description={props.t('campaigns.empty.description')}
                    />
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={campaigns}
                      isGlobalFilter={false}
                      isAddUserList={false}
                      pageSize={10}
                      totalPages={meta.totalPages}
                      count={meta.totalItems}
                      activePage={meta.currentPage}
                      setPage={(pageNumber) => setPage(pageNumber)}
                      className="custom-header-css"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <CreateCampaignWizard
          isOpen={showCreateModal}
          toggle={() => setShowCreateModal(!showCreateModal)}
          onSuccess={handleCampaignCreated}
        />

        <ToastContainer />
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(Campaigns);
